import React from 'react';

import { Button, Checkbox, Menu } from '@mantine/core';
import { Table } from '@tanstack/react-table';
import { Eye } from 'lucide-react';

interface ColumnVisibilityMenuProps<TData> {
  table: Table<TData>;
}

const COLUMN_LABELS: Record<string, string> = {
  // Outer (clonotype) table
  networkMap: 'Network Map',
  clonotypeId: 'Clonotype ID',
  clonotypeName: 'Clonotype Name',
  clonotypeSize: 'Clonotype size',
  numberOfNodes: 'Number of nodes',
  igIsotype: 'Ig isotype',
  vjGeneHeavy: 'V-J gene (heavy)',
  vjGeneLight: 'V-J gene (light)',
  representativeCDRH3: 'Representative CDRH3',
  representativeCDRL3: 'Representative CDRL3',
  // Inner (sequence) table
  node: 'Node',
  nodeSize: 'Node size',
  aps: 'APS',
  liabilities: 'Liabilities',
  shms: 'SHMs',
  heavyChain: 'Heavy Chain Sequence',
  lightChain: 'Light Chain Sequence',
  // Export summary table
  exportNums: 'Export',
  cdrh3: 'CDRH3',
  cdrl3: 'CDRL3',
  // Legacy / other
  id: 'ID',
  heavyVGene: 'Heavy V Gene',
  heavyJGene: 'Heavy J Gene',
  lightVGene: 'Light V Gene',
  lightJGene: 'Light J Gene',
};

const s = {
  triggerIcon: 'w-3.5 h-3.5',
  dropdown: 'max-h-96 overflow-y-auto',
  row: 'flex items-center gap-2 cursor-pointer w-full',
  rowLabel: 'text-xs',
  checkbox: 'pointer-events-none',
} as const;

export function ColumnVisibilityMenu<TData>({ table }: ColumnVisibilityMenuProps<TData>) {
  const handleToggleColumn = (columnId: string, currentVisibility: boolean) => {
    table.setColumnVisibility((prev) => ({
      ...prev,
      [columnId]: !currentVisibility,
    }));
  };

  return (
    <Menu>
      <Menu.Target>
        <Button variant="subtle" size="compact-xs" c="#3d3d3d" styles={{ section: { marginInlineEnd: 4 } }} leftSection={<Eye className={s.triggerIcon} />}>
          Columns
        </Button>
      </Menu.Target>
      <Menu.Dropdown className={s.dropdown}>
        <Menu.Label>Toggle Column Visibility</Menu.Label>
        {table.getAllLeafColumns().map((column) => {
          // `getCanHide()` is false for columns declared with `enableHiding: false`
          if (!column.id || !column.getCanHide() || column.id === 'expander' || column.id === 'select' || column.id === 'verticalSplitter') {
            return null;
          }

          const isVisible = column.getIsVisible();
          const label = COLUMN_LABELS[column.id] || column.id;

          return (
            <Menu.Item key={column.id} closeMenuOnClick={false}>
              <div
                className={s.row}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleToggleColumn(column.id, isVisible);
                }}
              >
                <Checkbox checked={isVisible} readOnly color="#0064B0" className={s.checkbox} />
                <span className={s.rowLabel}>{label}</span>
              </div>
            </Menu.Item>
          );
        })}
        <Menu.Divider />
        <Menu.Item onClick={() => table.toggleAllColumnsVisible(true)}>Show All Columns</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
